import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import Icon from '../../components/AppIcon';
import WorkshopFullScreenSheet from './WorkshopFullScreenSheet';
import { MILESTONE_MODAL_TYPES } from './milestoneModalConfig';
import {
  CUT_MODE_OPTIONS,
  MILESTONE_INTERACTION,
  PRINTING_OUTCOME_OPTIONS,
  getMilestoneInteractionKind,
  getMilestoneResultSummary,
  resolvePrintingOutcomeForPhase,
} from '../../lib/milestoneOutcomeUtils';
import MilestoneNameChoiceField from '../../components/milestones/MilestoneNameChoiceField';
import {
  isMilestoneNameChoicePending,
  requiresMilestoneNameSelection,
  resolveMilestoneDisplayName,
} from '../../lib/milestoneNameOptions';

export default function WorkshopMilestoneActionSheet({
  open,
  onClose,
  milestone,
  phase,
  modalType,
  nameOptions = [],
  genericLabel = '',
  readOnly = false,
  saving = false,
  onValidate,
  onReject,
  onOpenProductionSheet,
  onOpenIpr,
}) {
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [failedAligners, setFailedAligners] = useState('');
  const [pendingOutcome, setPendingOutcome] = useState('');
  const [pendingNameChoice, setPendingNameChoice] = useState('');
  const interactionKind = useMemo(
    () => (milestone && phase ? getMilestoneInteractionKind(milestone, phase) : MILESTONE_INTERACTION.SIMPLE),
    [milestone, phase]
  );

  const needsNamePick = requiresMilestoneNameSelection(nameOptions);
  const cutModeCatalogOptions = useMemo(() => {
    const opts = (nameOptions || []).filter(
      (o) =>
        o.category === 'cut_mode' ||
        o.code === 'laser' ||
        o.code === 'manuel' ||
        /^(laser|manuel)$/i.test(String(o.label || '').trim())
    );
    return opts.length >= 2 ? opts : [];
  }, [nameOptions]);

  const showCutModeFromCatalog = cutModeCatalogOptions.length >= 2;
  const showCutModeLegacy =
    !needsNamePick &&
    !showCutModeFromCatalog &&
    interactionKind === MILESTONE_INTERACTION.CUT_MODE;
  const showCutModeButtons = showCutModeLegacy || showCutModeFromCatalog;

  useEffect(() => {
    if (!open || !milestone) return;
    setRejectMode(false);
    setRejectNote('');
    setFailedAligners(
      milestone.outcome_quantity != null ? String(milestone.outcome_quantity) : ''
    );
    setPendingOutcome('');
    const current = milestone.name || milestone.milestone_name || '';
    setPendingNameChoice(
      nameOptions.some((o) => o.label === current) ? current : ''
    );
  }, [open, milestone?.id, nameOptions]);

  if (!milestone) return null;

  const isDone = milestone.completed === true;
  const isRejected =
    milestone.is_rejected === true || milestone.rejected_date != null;
  const viewOnly = readOnly || isDone;

  const inheritedOutcome =
    !milestone.completion_type && phase?.milestones
      ? resolvePrintingOutcomeForPhase(phase.milestones, milestone)
      : null;

  const resultSummary = getMilestoneResultSummary({
    ...milestone,
    completion_type: milestone.completion_type || inheritedOutcome,
  });

  const isSpecialOpen =
    modalType === MILESTONE_MODAL_TYPES.PRODUCTION_SHEET ||
    modalType === MILESTONE_MODAL_TYPES.IPR;

  const submitValidate = (extra = {}) => {
    if (viewOnly || !onValidate) return;
    const payload = { ...extra };
    if (needsNamePick && !payload.nameChoice) {
      if (!pendingNameChoice) {
        toast.error('Choisissez une valeur avant de valider.');
        return;
      }
      payload.nameChoice = pendingNameChoice;
    }
    onValidate(milestone, phase, payload);
  };

  const handleSimpleValidate = () => {
    if (isSpecialOpen && modalType === MILESTONE_MODAL_TYPES.PRODUCTION_SHEET && onOpenProductionSheet) {
      onOpenProductionSheet(milestone, {});
      return;
    }
    if (isSpecialOpen && modalType === MILESTONE_MODAL_TYPES.IPR && onOpenIpr) {
      onOpenIpr(milestone, {});
      return;
    }
    submitValidate({});
  };

  const handleOutcomeValidate = (outcome) => {
    if (outcome === 'echec') {
      const q = Number(failedAligners);
      if (!failedAligners.trim() || Number.isNaN(q) || q < 0) return;
      submitValidate({ outcomeChoice: 'echec', failedAligners: q });
      return;
    }
    submitValidate({ outcomeChoice: outcome });
  };

  const handleModeValidate = (mode) => {
    submitValidate({ modeChoice: mode });
  };

  const handleConfirmReject = () => {
    if (!rejectNote.trim()) return;
    if (onReject) onReject(milestone, phase, { rejectNote: rejectNote.trim() });
  };

  const footer = viewOnly ? (
    <button
      type="button"
      onClick={onClose}
      className="w-full min-h-[48px] rounded-xl border border-border font-medium text-sm cursor-pointer hover:bg-muted"
    >
      Fermer
    </button>
  ) : rejectMode ? (
    <div className="space-y-3">
      <label className="block space-y-1">
        <span className="text-sm font-medium text-foreground">
          Commentaire <span className="text-destructive">*</span>
        </span>
        <textarea
          value={rejectNote}
          onChange={(e) => setRejectNote(e.target.value)}
          rows={4}
          placeholder="Décrivez le problème rencontré…"
          className="w-full px-3 py-2 rounded-xl border border-border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-destructive/40"
          autoFocus
        />
      </label>
      <button
        type="button"
        onClick={handleConfirmReject}
        disabled={saving || !rejectNote.trim()}
        className="w-full min-h-[48px] rounded-xl bg-destructive text-destructive-foreground font-semibold text-sm hover:bg-destructive/90 disabled:opacity-50"
      >
        Confirmer le signalement
      </button>
      <button
        type="button"
        onClick={() => setRejectMode(false)}
        disabled={saving}
        className="w-full min-h-[44px] rounded-xl border border-border text-sm font-medium hover:bg-muted"
      >
        Annuler
      </button>
    </div>
  ) : (
    <div className="flex flex-col gap-2">
      {needsNamePick && !showCutModeFromCatalog ? (
        <MilestoneNameChoiceField
          options={nameOptions}
          value={pendingNameChoice}
          onChange={setPendingNameChoice}
          disabled={saving}
          label="Choisir une valeur"
        />
      ) : null}

      {interactionKind === MILESTONE_INTERACTION.OUTCOME && (
        <div className="space-y-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Résultat
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {PRINTING_OUTCOME_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                disabled={saving}
                onClick={() => {
                  if (opt.value === 'echec') {
                    setPendingOutcome('echec');
                  } else {
                    handleOutcomeValidate(opt.value);
                  }
                }}
                className={`min-h-[52px] rounded-xl text-sm font-semibold border cursor-pointer transition-colors ${opt.segmentClass}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {(pendingOutcome === 'echec' || milestone.completion_type === 'echec') && (
            <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-3 space-y-2">
              <label className="block text-sm font-medium text-foreground">
                Nombre d&apos;aligneurs en échec <span className="text-destructive">*</span>
              </label>
              <input
                type="number"
                min={0}
                step={1}
                value={failedAligners}
                onChange={(e) => setFailedAligners(e.target.value)}
                className="w-full min-h-[44px] px-3 rounded-lg border border-border bg-background text-base"
                placeholder="0"
              />
              <button
                type="button"
                disabled={saving || !failedAligners.trim()}
                onClick={() => handleOutcomeValidate('echec')}
                className="w-full min-h-[48px] rounded-xl bg-destructive text-destructive-foreground font-semibold text-sm disabled:opacity-50"
              >
                Valider l&apos;échec
              </button>
            </div>
          )}
        </div>
      )}

      {showCutModeButtons && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Mode de découpe
          </p>
          <div className="grid grid-cols-2 gap-2">
            {showCutModeFromCatalog
              ? cutModeCatalogOptions.map((opt) => (
                  <button
                    key={opt.id || opt.code}
                    type="button"
                    disabled={saving}
                    onClick={() =>
                      submitValidate({
                        nameChoice: opt.label,
                        modeChoice:
                          opt.code === 'laser' || opt.code === 'manuel'
                            ? opt.code
                            : /laser/i.test(opt.label)
                              ? 'laser'
                              : /manuel/i.test(opt.label)
                                ? 'manuel'
                                : undefined,
                      })
                    }
                    className={`min-h-[52px] rounded-xl text-sm font-semibold border cursor-pointer ${
                      CUT_MODE_OPTIONS.find((c) => c.value === opt.code)?.segmentClass ||
                      'bg-muted text-foreground border-border'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))
              : CUT_MODE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={saving}
                    onClick={() => handleModeValidate(opt.value)}
                    className={`min-h-[52px] rounded-xl text-sm font-semibold border cursor-pointer ${opt.segmentClass}`}
                  >
                    {opt.label}
                  </button>
                ))}
          </div>
        </div>
      )}

      {(interactionKind === MILESTONE_INTERACTION.SIMPLE ||
        (needsNamePick && !showCutModeButtons && interactionKind !== MILESTONE_INTERACTION.OUTCOME)) && (
        <button
          type="button"
          onClick={handleSimpleValidate}
          disabled={saving || (needsNamePick && !pendingNameChoice)}
          className="w-full min-h-[52px] rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {saving ? (
            <>
              <span className="animate-spin h-4 w-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full" />
              Enregistrement…
            </>
          ) : (
            <>
              <Icon name="CheckCircle2" size={18} />
              {isSpecialOpen && modalType === MILESTONE_MODAL_TYPES.PRODUCTION_SHEET
                ? 'Ouvrir la fiche de production'
                : isSpecialOpen && modalType === MILESTONE_MODAL_TYPES.IPR
                  ? 'Ouvrir la fiche stripping'
                  : 'Valider'}
            </>
          )}
        </button>
      )}

      {onReject ? (
        <button
          type="button"
          onClick={() => setRejectMode(true)}
          disabled={saving}
          className="w-full min-h-[48px] rounded-xl border border-destructive/40 text-destructive font-medium text-sm hover:bg-destructive/10 disabled:opacity-50"
        >
          Signaler un problème
        </button>
      ) : null}
    </div>
  );

  return (
    <WorkshopFullScreenSheet
      open={open}
      onClose={onClose}
      title={resolveMilestoneDisplayName(milestone, nameOptions, genericLabel)}
      subtitle={phase?.name}
      footer={footer}
    >
      <div className="space-y-4">
        <p
          className={`text-xs font-medium uppercase tracking-wide ${
            isDone
              ? 'text-success'
              : isRejected
                ? 'text-destructive'
                : 'text-muted-foreground'
          }`}
        >
          {isDone ? 'Validé' : isRejected ? 'Problème signalé' : 'À valider'}
        </p>

        {milestone.validatedByName && isDone ? (
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Icon name="UserCheck" size={16} className="text-primary shrink-0" />
            Validé par {milestone.validatedByName}
          </p>
        ) : null}

        {resultSummary ? (
          <p className="text-sm text-foreground font-medium leading-relaxed">{resultSummary}</p>
        ) : milestone.description ? (
          <p className="text-sm text-muted-foreground leading-relaxed">{milestone.description}</p>
        ) : null}

        {isRejected && milestone.note && !isDone ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            {milestone.note}
          </div>
        ) : null}

        {!viewOnly && needsNamePick && isMilestoneNameChoicePending(milestone, nameOptions) ? (
          <p className="text-xs font-medium text-amber-600 dark:text-amber-400 rounded-lg bg-amber-500/10 border border-amber-500/30 px-3 py-2">
            Choix requis avant validation.
          </p>
        ) : null}

        {!viewOnly && interactionKind === MILESTONE_INTERACTION.SIMPLE && !isSpecialOpen && !needsNamePick && (
          <p className="text-xs text-muted-foreground rounded-lg bg-muted/40 p-3 leading-relaxed">
            Validez l&apos;étape une fois le travail effectué, ou signalez un problème avec un
            commentaire obligatoire.
          </p>
        )}
      </div>
    </WorkshopFullScreenSheet>
  );
}
